import React from "react";
import { Link } from "./PageLink";
import { Helmet } from "react-helmet-async";
import ProcessStrip from "./ProcessStrip";
import AreaGrid from "./AreaGrid";
import CTABand from "./CTABand";
import FAQ, { QA } from "./FAQ";
import Picture, { type ImgSet } from "./Picture";
import ImageStrip, { type StripItem } from "./ImageStrip";
import { CLUSTERS, SERVICES } from "../lib/areas";

interface Tile { title: string; body: string; }

interface Example { image: ImgSet; alt: string; heading: string; body: string; }

interface ServicePageProps {
  slug: string;
  title: string;
  metaDescription: string;
  intro: React.ReactNode;
  included: Tile[];
  useCases: Tile[];
  exampleImage?: ImgSet;
  exampleAlt?: string;
  exampleHeading?: string;
  exampleBody?: string;
  extraExamples?: Example[];
  faqs: QA[];
  serviceTypeSchema: string;
  schemaDescription: string;
  stripItems?: StripItem[];
  stripEyebrow?: string;
  stripHeading?: string;
  hideProcessStrip?: boolean;
  hideRelatedServices?: boolean;
  processSteps?: Tile[];
  afterIncluded?: React.ReactNode;
  additionalTypeSchema?: string;
  projectComparison?: React.ReactNode;
}

const ServicePage: React.FC<ServicePageProps> = ({
  slug, title, metaDescription, intro,
  included, useCases, exampleImage, exampleAlt, exampleHeading, exampleBody, extraExamples,
  faqs, serviceTypeSchema, schemaDescription,
  stripItems, stripEyebrow, stripHeading,
  hideProcessStrip, hideRelatedServices,
  processSteps, afterIncluded, additionalTypeSchema, projectComparison,
}) => {
  const related = SERVICES.filter((s) => s.slug !== slug);
  const allAreaNames = CLUSTERS.flatMap((c) => c.cities);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: title,
    serviceType: serviceTypeSchema,
    ...(additionalTypeSchema ? { additionalType: additionalTypeSchema } : {}),
    description: schemaDescription,
    provider: { "@id": "https://jandsav.com/#localbusiness" },
    areaServed: allAreaNames,
  };

  return (
    <>
      <Helmet>
        <title>{`J&S Audio Video Solutions – ${title} – Central MA`}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={`J&S Audio Video Solutions – ${title}`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <section className="subhero" data-testid={`subhero-${slug}`}>
        <div className="container">
          <span className="eyebrow" style={{ color: "var(--color-accent)" }}>Service</span>
          <h1>{title}</h1>
          <p className="lead">{intro}</p>
          <div className="hero-ctas" style={{ marginTop: "var(--space-4)" }}>
            <Link to="/contact" className="btn" data-testid="link-service-cta">Request a Consultation</Link>
            <a href="tel:+15084500490" className="btn btn-outline" data-testid="link-service-phone">Call 508-450-0490</a>
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <span className="eyebrow">What's included</span>
          <h2>The pieces of a system done right.</h2>
          <div className="tile-grid mt-5">
            {included.map((t) => (
              <div className="tile" key={t.title}>
                <h3>{t.title}</h3>
                <p>{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {afterIncluded}
      {stripItems && stripItems.length > 0 && (
        <section className="section section-light" data-testid={`service-strip-${slug}`}>
          <div className="container">
            <span className="eyebrow">{stripEyebrow ?? "The work"}</span>
            <h2>{stripHeading ?? "A wider look at what we install."}</h2>
            <ImageStrip items={stripItems} />
          </div>
        </section>
      )}
      {!hideProcessStrip && (
      <section className="section">
        <div className="container">
          <span className="eyebrow">How we work</span>
          <h2>A clear process, start to finish.</h2>
          <ProcessStrip steps={processSteps} />
        </div>
      </section>
      )}
      <section className="section section-light">
        <div className="container">
          <span className="eyebrow">When you need this</span>
          <h2>Common reasons people call us.</h2>
          <div className="tile-grid mt-5">
            {useCases.map((t) => (
              <div className="tile" key={t.title}>
                <h3>{t.title}</h3>
                <p>{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {projectComparison}
      {exampleImage && exampleHeading && (
      <section className="section">
        <div className="container">
          <div className="split">
            <div>
              <Picture
                src={exampleImage}
                alt={exampleAlt ?? exampleHeading}
                width={1200}
                height={900}
                className="split-img"
                sizes="(max-width: 768px) 100vw, 600px"
              />
            </div>
            <div>
              <span className="eyebrow">Real-world example</span>
              <h2>{exampleHeading}</h2>
              <p style={{ marginTop: "1rem" }}>{exampleBody}</p>
            </div>
          </div>
          {extraExamples?.map((ex) => (
            <div className="split" key={ex.heading} style={{ marginTop: "var(--space-7)" }}>
              <div>
                <span className="eyebrow">Real-world example</span>
                <h2>{ex.heading}</h2>
                <p style={{ marginTop: "1rem" }}>{ex.body}</p>
              </div>
              <div>
                <Picture
                  src={ex.image}
                  alt={ex.alt}
                  width={1200}
                  height={900}
                  className="split-img"
                  sizes="(max-width: 768px) 100vw, 600px"
                />
              </div>
            </div>
          ))}
        </div>
      </section>
      )}
      {!hideRelatedServices && (
      <section className="section section-light">
        <div className="container">
          <span className="eyebrow">Related services</span>
          <h2>Often paired with this work.</h2>
          <div className="card-grid card-grid-3 mt-5">
            {related.map((r) => (
              <article className="card" key={r.slug} data-testid={`link-related-${r.slug}`}>
                <div className="card-content">
                  <h3 className="card-title">{r.title}</h3>
                  <p className="card-desc">{r.short}</p>
                  <Link to={r.path} className="card-link">Explore {r.title}</Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      )}
      <section className="section">
        <div className="container">
          <span className="eyebrow">Where We Work</span>
          <h2>Central Massachusetts & Parts of MetroWest Boston</h2>
          <AreaGrid />
        </div>
      </section>
      <section className="section section-light">
        <div className="container container-sm">
          <span className="eyebrow">Frequently asked</span>
          <h2>Questions we hear most.</h2>
          <FAQ items={faqs} />
        </div>
      </section>
      <CTABand
        heading={`Ready to talk about ${title.toLowerCase()}?`}
        body="Tell us about the room. We'll come back with a clean, honest plan."
      />
    </>
  );
};

export default ServicePage;
